import axios from 'axios';
import {scrapeWebsite,extractDomain,formatCompanyName} from './websiteAnalyzer';
import { generateSequentialOpener, regenerateSequentialOpener } from './sequentialOpenerGenerator';
import { generateFiveLineOpener, regenerateFiveLineOpener } from './fiveLineOpenerGenerator';
import { scrapeLinkedInProfile, isValidLinkedInUrl, extractLinkedInUsername, processLinkedInDataForOpeners } from './linkedinScraper';
import { scrapeTalkingPoints, isValidWebsiteForTalkingPoints } from './talkingPointsScraper';

/**
 * Parse JSON string that might be wrapped in markdown code blocks
 * @param {string} jsonString - The JSON string to parse, potentially wrapped in markdown
 * @returns {Object|null} - Parsed JSON object or null if parsing fails
 */
export const parseJsonResponse = (jsonString) => {
  try {
    // Check if the string is wrapped in markdown code blocks
    let cleanJson = jsonString;
    
    // Remove markdown code block syntax if present
    const codeBlockRegex = /```(?:json)?\s*([\s\S]*?)\s*```/;
    const match = jsonString.match(codeBlockRegex);
    if (match && match[1]) {
      cleanJson = match[1].trim();
    }
    
    // Parse the cleaned JSON string
    return JSON.parse(cleanJson);
  } catch (error) {
    console.error('Error parsing JSON response:', error);
    return null;
  }
};

/** 
 * Generate a personalized opener using AI
 * @param {Object} websiteData - Website data including URL, company name, etc.
 * @param {Object} config - API configuration (apiKey, model, endpoint)
 * @param {Object} openerConfig - Optional opener generation settings
 * @returns {Promise<string>} - AI generated opener
 */
export const generatePersonalizedOpener = async (websiteData, config, openerConfig = null) => {
  try {
    // Prepare the context for the AI
    const companyName = formatCompanyName(websiteData.company, websiteData.url);
    const summary = websiteData.summary;
    const linkedInData = websiteData.linkedInData || null;
    const talkingPointsData = websiteData.talkingPointsData || null;
    const qualificationResult = websiteData.qualificationResult || null;
    
    // Ensure we have sufficient data for opener generation
    if (!summary || summary === 'No company information available') {
      throw new Error('Insufficient data for opener generation: No company information available');
    }

    // Apply opener configuration if provided
    const style = openerConfig?.style || 'casual';
    const length = openerConfig?.length || 'medium';
    const focus = openerConfig?.focus || 'refresh';
    const serviceType = openerConfig?.serviceType || 'website';
    const subServiceType = openerConfig?.subServiceType || null;
    const manualFocus = openerConfig?.manualFocus || '';
    const strategy = openerConfig?.strategy || '8step';
    
    // Use the appropriate strategy based on the configuration
    if (strategy === '5line') {
      console.log('Using 5-line template strategy for opener generation');
      return await generateFiveLineOpener(websiteData, config, openerConfig);
    } else if (strategy === '8step') {
      console.log('Using 8-step sequential strategy for opener generation');
      return await generateSequentialOpener(websiteData, config, openerConfig);
    }

    // Get fresh website data if needed
    let websiteContent = websiteData;
    if (!websiteData.content) {
      console.log('Scraping fresh website data for opener generation...');
      const scrapedData = await scrapeWebsite(websiteData.url, false); // Always false for screenshots
      
      // Check if scraping failed and throw error
      if (scrapedData.source === 'fallback' && scrapedData.error) {
        throw new Error(`Fresh website scraping failed: ${scrapedData.error}`);
      }
      
      websiteContent = {...websiteData, ...scrapedData};
      console.log('Successfully scraped website data:', websiteContent);
    }

    // Create service-specific prompt
    let serviceSpecificInstructions = '';
    let systemPrompt = '';
    let objective = '';
    let triggerType = '';

    if (serviceType === 'website') {
      objective = "get a reply to share a one-pager showing website redesign examples";
      triggerType = "website content/offerings that need improvement";
      serviceSpecificInstructions = `
        Focus on delivering immediate value by identifying website improvement opportunities that would genuinely help their business based on their content and offerings.
        ${focus === 'refresh' ? '- Start with a specific, valuable insight about their website content that could be refreshed\n- Demonstrate your expertise by highlighting how refreshing their content would directly benefit their business metrics' : ''}
        ${focus === 'financing' ? '- Start with a specific, valuable insight about their website content or offerings\n- Demonstrate your expertise by highlighting how our 12-month financing options could help them afford a comprehensive website redesign' : ''}
        ${focus === 'conversion' ? '- Start with a specific, valuable insight about their conversion elements based on content\n- Demonstrate your expertise by highlighting how optimizing these elements would directly increase their revenue' : ''}
        ${focus === 'mobile' ? '- Start with a specific, valuable insight about their mobile experience based on content\n- Demonstrate your expertise by highlighting how improving mobile responsiveness would directly increase engagement and conversions' : ''}
        ${focus === 'branding' ? '- Start with a specific, valuable insight about their brand consistency based on content\n- Demonstrate your expertise by highlighting how strengthening their content messaging would improve customer trust' : ''}
        ${focus === 'accessibility' ? '- Start with a specific, valuable insight about their website accessibility based on content\n- Demonstrate your expertise by highlighting how improving accessibility would expand their audience and comply with standards' : ''}
        ${focus === 'manual' && manualFocus ? `- Start with a specific, valuable insight about their ${manualFocus}\n- Demonstrate your expertise by highlighting how improving their ${manualFocus} would directly benefit their business` : ''}
      `;
      
      systemPrompt = `You are a results-focused web design consultant who writes value-first, ${style}, conversational openers. You focus on ${focus === 'manual' ? manualFocus : focus} improvements with ${length} messages that lead with specific, valuable insights and demonstrate your expertise without sounding like a sales pitch.`;
    } else if (serviceType === 'onboarding') {
      const platform = subServiceType === 'apollo' ? 'Apollo.io' : 'Instantly.ai';
      objective = `get a reply to share how ${platform} can specifically help their business`;
      triggerType = "sales or outreach challenges visible in their content";
      
      serviceSpecificInstructions = `
        Focus on delivering immediate value by identifying how ${platform} could solve their specific business challenges based on their content and offerings.
        ${subServiceType === 'apollo' ? `
          ${focus === 'prospecting' ? '- Start with a specific, valuable insight about their prospecting challenges visible in their content\n- Demonstrate your expertise by highlighting how Apollo could help them find better-fit prospects in their specific industry' : ''}
          ${focus === 'engagement' ? '- Start with a specific, valuable insight about their engagement challenges visible in their content\n- Demonstrate your expertise by highlighting how Apollo could help them engage with prospects more effectively' : ''}
          ${focus === 'analytics' ? '- Start with a specific, valuable insight about their data or analytics challenges visible in their content\n- Demonstrate your expertise by highlighting how Apollo\'s insights could help them make better decisions' : ''}
          ${focus === 'integration' ? '- Start with a specific, valuable insight about their workflow challenges visible in their content\n- Demonstrate your expertise by highlighting how Apollo integrates with their existing tools' : ''}
          ${focus === 'efficiency' ? '- Start with a specific, valuable insight about their time management challenges visible in their content\n- Demonstrate your expertise by highlighting how Apollo automates repetitive prospecting tasks' : ''}
          ${focus === 'targeting' ? '- Start with a specific, valuable insight about their audience targeting challenges visible in their content\n- Demonstrate your expertise by highlighting how Apollo provides precise targeting options' : ''}
          ${focus === 'manual' && manualFocus ? `- Start with a specific, valuable insight about their ${manualFocus}\n- Demonstrate your expertise by highlighting how Apollo could help with their ${manualFocus}` : ''}
        ` : `
          ${focus === 'deliverability' ? '- Start with a specific, valuable insight about their email deliverability challenges visible in their content\n- Demonstrate your expertise by highlighting how Instantly.ai could improve their cold email deliverability specifically' : ''}
          ${focus === 'automation' ? '- Start with a specific, valuable insight about their outreach processes visible in their content\n- Demonstrate your expertise by highlighting how Instantly.ai could automate their cold email outreach to save them time' : ''}
          ${focus === 'scaling' ? '- Start with a specific, valuable insight about their scaling challenges visible in their content\n- Demonstrate your expertise by highlighting how Instantly.ai could help them scale their cold email outreach efforts' : ''}
          ${focus === 'personalization' ? '- Start with a specific, valuable insight about their personalization challenges visible in their content\n- Demonstrate your expertise by highlighting how Instantly.ai enables dynamic personalization at scale' : ''}
          ${focus === 'analytics' ? '- Start with a specific, valuable insight about their email performance tracking challenges visible in their content\n- Demonstrate your expertise by highlighting how Instantly.ai provides detailed analytics' : ''}
          ${focus === 'compliance' ? '- Start with a specific, valuable insight about their email compliance challenges visible in their content\n- Demonstrate your expertise by highlighting how Instantly.ai helps maintain email regulation compliance' : ''}
          ${focus === 'manual' && manualFocus ? `- Start with a specific, valuable insight about their ${manualFocus}\n- Demonstrate your expertise by highlighting how Instantly.ai could help with their ${manualFocus}` : ''}
        `}
      `;

      const platformDescription = subServiceType === 'apollo' ? 'sales intelligence platform' : 'cold email outreach platform';
      systemPrompt = `You are a results-focused sales enablement advisor who writes value-first, ${style}, authentic conversation starters about ${platform}, a ${platformDescription}. You focus on ${focus === 'manual' ? manualFocus : focus} with ${length} messages that lead with specific, valuable insights and demonstrate your expertise without sounding like a sales pitch.`;
    } else if (serviceType === 'cold_email') {
      objective = "get a reply to share a one-pager on cold email systems";
      triggerType = "lead generation challenges visible in their content";
      
      serviceSpecificInstructions = `
        Focus on delivering immediate value by identifying how a cold email system could genuinely supplement their current lead generation based on their content and offerings.
        ${focus === 'good_way_to_supplement_leads' ? '- Start with a specific, valuable insight about gaps in their current lead generation visible in their content\n- Demonstrate your expertise by highlighting how cold emails could complement these specific approaches to increase leads' : ''}
        ${focus === 'targeting' ? '- Start with a specific, valuable insight about their targeting challenges visible in their content\n- Demonstrate your expertise by highlighting how targeted cold emails could help them reach specific audience segments' : ''}
        ${focus === 'high_roi' ? '- Start with a specific, valuable insight about their ROI challenges visible in their content\n- Demonstrate your expertise by highlighting how cold emails offer measurable ROI for their specific business model' : ''}
        ${focus === 'automation' ? '- Start with a specific, valuable insight about their manual outreach processes visible in their content\n- Demonstrate your expertise by highlighting how cold email automation could save them significant time' : ''}
        ${focus === 'personalization' ? '- Start with a specific, valuable insight about their personalization challenges visible in their content\n- Demonstrate your expertise by highlighting how cold emails can be highly personalized at scale' : ''}
        ${focus === 'analytics' ? '- Start with a specific, valuable insight about their lead tracking challenges visible in their content\n- Demonstrate your expertise by highlighting how cold email systems provide detailed performance analytics' : ''}
        ${focus === 'manual' && manualFocus ? `- Start with a specific, valuable insight about their ${manualFocus}\n- Demonstrate your expertise by highlighting how cold email systems could help with their ${manualFocus}` : ''}
      `;

      systemPrompt = `You are a results-focused outreach advisor who writes value-first, ${style}, authentic conversation starters about cold email systems. You focus on ${focus === 'manual' ? manualFocus : focus} with ${length} messages that lead with specific, valuable insights and demonstrate your expertise without sounding like a sales pitch.`;
    } else if (serviceType === 'aeo') {
      objective = "get a reply to share insights on AI Engine Optimization (AEO) for their website";
      triggerType = "website content that could be optimized for AI search engines and chatbots";
      
      serviceSpecificInstructions = `
        Focus on delivering immediate value by identifying how AI Engine Optimization (AEO) could help their website rank better in AI-powered search results and chatbot responses based on their content and offerings.
        ${focus === 'ai_visibility' ? '- Start with a specific, valuable insight about their website\'s AI discoverability based on content\n- Demonstrate your expertise by highlighting how AEO could improve their visibility in AI search results and chatbot recommendations' : ''}
        ${focus === 'content_structure' ? '- Start with a specific, valuable insight about their content structure for AI consumption based on their website\n- Demonstrate your expertise by highlighting how structured data and AI-friendly content formatting would improve their AI search rankings' : ''}
        ${focus === 'semantic_optimization' ? '- Start with a specific, valuable insight about their semantic content optimization based on their website\n- Demonstrate your expertise by highlighting how semantic markup and entity optimization would help AI engines better understand their business' : ''}
        ${focus === 'voice_search' ? '- Start with a specific, valuable insight about their voice search readiness based on content\n- Demonstrate your expertise by highlighting how AEO techniques could improve their visibility in voice search and AI assistant responses' : ''}
        ${focus === 'ai_snippets' ? '- Start with a specific, valuable insight about their content\'s potential for AI-generated snippets\n- Demonstrate your expertise by highlighting how optimizing for AI snippet generation could increase their organic visibility' : ''}
        ${focus === 'chatbot_optimization' ? '- Start with a specific, valuable insight about their content\'s chatbot discoverability\n- Demonstrate your expertise by highlighting how AEO could make their business more likely to be recommended by AI chatbots' : ''}
        ${focus === 'manual' && manualFocus ? `- Start with a specific, valuable insight about their ${manualFocus}\n- Demonstrate your expertise by highlighting how AEO techniques could help with their ${manualFocus}` : ''}
      `;

      systemPrompt = `You are a results-focused AI Engine Optimization (AEO) consultant who writes value-first, ${style}, conversational openers about optimizing websites for AI search engines and chatbots. You focus on ${focus === 'manual' ? manualFocus : focus} with ${length} messages that lead with specific, valuable insights and demonstrate your expertise without sounding like a sales pitch.`;
    } else if (serviceType === 'partnership') {
      objective = "get a reply to share a case study about a previous partnership";
      triggerType = "agencies or businesses that might need web development team support";
      
      serviceSpecificInstructions = `
        Focus on delivering immediate value by identifying how a web development partnership could help their agency or business scale without building an in-house team, based on their content and offerings.
        ${focus === 'scaling' ? '- Start with a specific, valuable insight about their scaling challenges visible in their content\n- Demonstrate your expertise by highlighting how a development partnership could help them take on more clients without hiring overhead' : ''}
        ${focus === 'expertise' ? '- Start with a specific, valuable insight about their technical expertise gaps visible in their content\n- Demonstrate your expertise by highlighting how a development partnership provides specialized skills they might be missing' : ''}
        ${focus === 'capacity' ? '- Start with a specific, valuable insight about their capacity constraints visible in their content\n- Demonstrate your expertise by highlighting how a development partnership could handle overflow work during busy periods' : ''}
        ${focus === 'cost_efficiency' ? '- Start with a specific, valuable insight about their cost management challenges visible in their content\n- Demonstrate your expertise by highlighting how partnerships are more cost-effective than building an in-house team' : ''}
        ${focus === 'client_retention' ? '- Start with a specific, valuable insight about their client service challenges visible in their content\n- Demonstrate your expertise by highlighting how reliable development partnerships help maintain client satisfaction' : ''}
        ${focus === 'time_to_market' ? '- Start with a specific, valuable insight about their project delivery timelines visible in their content\n- Demonstrate your expertise by highlighting how development partnerships can accelerate project completion' : ''}
        ${focus === 'website_management' ? '- Start with a specific, valuable insight about their website management challenges visible in their content\n- Demonstrate your expertise by highlighting how a development partnership can provide ongoing website management and maintenance services' : ''}
        ${focus === 'manual' && manualFocus ? `- Start with a specific, valuable insight about their ${manualFocus}\n- Demonstrate your expertise by highlighting how a development partnership could help with their ${manualFocus}` : ''}
      `;

      systemPrompt = `You are a results-focused web development partnership consultant who writes value-first, ${style}, conversational openers about partnering with agencies and businesses that need development team support. You focus on ${focus === 'manual' ? manualFocus : focus} with ${length} messages that lead with specific, valuable insights and demonstrate your expertise without sounding like a sales pitch.`;
    }

    // Create personalization talking points from scraped data
    let personalizationTalkingPoints = [];
    
    // Extract talking points from website data
    if (websiteContent.title) {
      personalizationTalkingPoints.push(`Website focus: ${websiteContent.title}`);
    }
    if (websiteContent.headings) {
      const mainHeadings = websiteContent.headings.split(',').slice(0, 2).join(', ');
      personalizationTalkingPoints.push(`Key offerings: ${mainHeadings}`);
    }
    
    // Extract talking points from LinkedIn data
    if (linkedInData) {
      if (linkedInData.headline) {
        personalizationTalkingPoints.push(`LinkedIn role: ${linkedInData.headline}`);
      }
      if (linkedInData.experience) {
        const recentExp = linkedInData.experience.substring(0, 100);
        personalizationTalkingPoints.push(`Recent experience: ${recentExp}`);
      }
    }
    
    // Extract talking points from business opportunities
    if (talkingPointsData && talkingPointsData.success && talkingPointsData.data?.opportunities) {
      const topOpportunities = talkingPointsData.data.opportunities.slice(0, 2);
      topOpportunities.forEach(opp => {
        personalizationTalkingPoints.push(`Business opportunity: ${opp}`);
      });
    }
    
    // Create prompt based on the website data and settings and the step-by-step copywriting workflow
    const prompt = `
${systemPrompt}

You are creating a value-first, conversational message to ${companyName}. Write a personalized ${style}, ${length} email opener for outreach to the following website:

Website: ${websiteData.url}
Company Name: ${companyName}

PERSONALIZATION TALKING POINTS (Use these as quick references to show you know them):
${personalizationTalkingPoints.length > 0 ? personalizationTalkingPoints.map(point => `• ${point}`).join('\n') : '• No specific talking points available'}

WEBSITE CONTEXT:
Website Title: ${websiteContent.title || 'Not available'}
Meta Description: ${websiteContent.metaDescription || 'Not available'}
Main Headings: ${websiteContent.headings || 'Not available'}
Navigation Elements: ${websiteContent.navigation || 'Not available'}
Website Content Sample: ${websiteContent.content ? websiteContent.content.substring(0, 800) : 'No content available'}

ADDITIONAL CONTEXT:
${linkedInData ? `LinkedIn Profile: ${linkedInData.headline || 'Not available'} | Experience: ${linkedInData.experience ? linkedInData.experience.substring(0, 200) : 'Not available'}` : 'No LinkedIn data available'}
${talkingPointsData && talkingPointsData.success ? `Business Summary: ${talkingPointsData.data?.websiteSummary || 'Not available'}` : 'No business analysis available'}

STEP-BY-STEP COPYWRITING WORKFLOW - YOU MUST FOLLOW THIS EXACTLY:

1) Define one single objective: ${objective}

2) ICP: Company with ${triggerType}
   Trigger: Identify a specific element from their website content to reference

3) Subject line: Create a 3-6 word subject line using one of these patterns:
   - "Quick idea for ${companyName}"
   - "${companyName} + [outcome]"
   - "Thought about your [trigger]"

4) Research-backed 1-line hook:
   - One short sentence using a PERSONALIZATION TALKING POINT from above to show you know them
   - Reference something specific from their website, LinkedIn, or business opportunities
   - Make it factual and truthful based on the scraped data
   - Use present-tense, active phrasing
   - MUST be just one sentence that grabs attention with a specific insight about THEM

5) State the core pain in one line:
   - Concise diagnosis that the prospect would recognize
   - Use their industry language where possible
   - MUST be exactly one line that identifies a problem

6) Offer a single specific outcome (one line):
   - Structure your offer based on the Template Strategy (${strategy}), Approach Style (${style}), Message Length (${length}), and Focus Area (${focus})
   - Promise something tangible and believable that aligns with the focus area
   - Use numbers or timelines when possible
   - Avoid vague claims
   - MUST be exactly one line that offers a clear solution tailored to their specific situation

7) Add one short proof line:
   - Name-drop a comparable company or specific metric
   - If you can't name clients, use a specific result like "Cut [metric] X% in Y days for a similar [industry] company"
   - Keep it measured, avoid hype
   - MUST be exactly one line that provides credibility

8) End with a low-commitment, value-driven CTA:
   - Offer immediate value like "I made a 2-min case study showing how this works - can I send it over?" or "I put together a quick Loom walkthrough - want me to share it?"
   - Provide examples: "I created a relevant example - interested?", "I have the 1-page breakdown ready - should I send it?", "I recorded a quick walkthrough - can I shoot it over?"
   - NEVER ask for calls or demos as the first ask
   - Focus on giving value, not getting something from them
   - MUST be a simple yes/no question that offers immediate value

9) Keep total length 3-6 short sentences (50-120 words)
   - People scan. Respect their time.
   - No long paragraphs.
   - Break lines for scannability.

FORMAT YOUR RESPONSE AS:
Subject: [3-6 word subject line]

[1-line hook] [1-line pain statement] [1-line outcome] [1-line proof] [1-line CTA]

TONE AND LANGUAGE REQUIREMENTS:
- Use friendly professional tone; no hype, no buzzwords
- Use contractions to sound natural (we're, don't)
- Avoid negative framing; lead with helpfulness
- Use active voice and short sentences
- Use SIMPLE language a 5th grader could understand
- Never use generic templates - be authentically specific to their website content

${serviceSpecificInstructions}

CRITICAL REQUIREMENTS:
1. DO NOT start with greetings like "Hey", "Hi", "Hello", etc. Start directly with a specific, valuable insight.
2. ALWAYS use at least ONE PERSONALIZATION TALKING POINT from the list above to show you know them personally
3. Reference something SPECIFIC from their scraped data (website, LinkedIn, or business opportunities) - NOT generic design elements
4. Be CONVERSATIONAL and HELPFUL - never pushy or sales-oriented
5. Focus on THEIR needs and pain points, not your services
6. Use SIMPLE language a 5th grader could understand - avoid jargon and complex terms
7. ALWAYS mention the company name (${companyName}) at least once
8. Structure your OFFER based on: Template Strategy (${strategy}), Approach Style (${style}), Message Length (${length}), Focus Area (${focus})
9. End with a gentle, non-pushy question or conversation starter that can be answered with a simple "yes"
10. NEVER use generic templates - be authentically specific to their scraped data
11. NEVER start with "I noticed" or "I saw" - start with the insight itself
12. LEAD WITH VALUE - give them something useful before asking for anything
13. Use the PERSONALIZATION TALKING POINTS as quick references to demonstrate you've researched them
14. Make your offer directly relevant to the Focus Area (${focus}) while maintaining the specified Approach Style (${style})
${serviceType === 'onboarding' ? `${(linkedInData ? 1 : 0) + (talkingPointsData && talkingPointsData.success ? 1 : 0) + 11}. Mention ${subServiceType === 'apollo' ? 'Apollo.io' : 'Instantly.ai'} naturally in your opener, but don't make it sound like a sales pitch` : ''}
${serviceType === 'cold_email' ? `${(linkedInData ? 1 : 0) + (talkingPointsData && talkingPointsData.success ? 1 : 0) + 11}. Mention cold email systems naturally in your opener, but don\'t make it sound like a sales pitch` : ''}
${(linkedInData ? 1 : 0) + (talkingPointsData && talkingPointsData.success ? 1 : 0) + 12}. STRICTLY follow the 8-step process, with each numbered step corresponding to exactly one sentence
${(linkedInData ? 1 : 0) + (talkingPointsData && talkingPointsData.success ? 1 : 0) + 13}. The first sentence MUST be a hook that references something specific from their website
${(linkedInData ? 1 : 0) + (talkingPointsData && talkingPointsData.success ? 1 : 0) + 14}. The last sentence MUST be a low-friction CTA that's easy to respond to
${(linkedInData ? 1 : 0) + (talkingPointsData && talkingPointsData.success ? 1 : 0) + 15}. The opener MUST be 3-6 sentences total, with each sentence serving a specific purpose

EXAMPLE FORMAT (DO NOT COPY THIS CONTENT, JUST THE STRUCTURE):
Subject: Quick idea for TechCorp

[HOOK using personalization talking point] Your "Enterprise Solutions" focus and recent expansion into AI consulting shows strong growth momentum. [PAIN] Most companies in your space struggle with mobile conversion rates that limit their growth potential. [OFFER structured by focus area] A targeted mobile optimization strategy could increase your conversion rates by 25-40% within 60 days. [PROOF] We've helped similar consulting firms boost mobile conversions by 32% using these specific techniques. [CTA] Want to see a 2-min case study of how we did this for a similar consulting firm?

Write ONLY the opener text with no additional commentary.
`;

    // Add /nothink suffix if option is enabled
    const finalPrompt = config.useNoThink ? prompt + "/nothink" : prompt;

    // Handle different API endpoints
    let result;
    try {
      // For OpenAI, OpenRouter, and Custom endpoints
      if (config.endpoint === 'openai' || config.endpoint === 'openrouter' || config.endpoint === 'custom') {
        if (!config.apiKey || !config.model) {
          console.warn('No API key or model provided for AI opener generation');
          throw new Error('API configuration required. Please add your API key and model in Settings.');
        }

        let apiUrl = '';
        let headers = {'Content-Type': 'application/json'};

        // Configure API endpoint based on provider
        switch (config.endpoint) {
          case 'openai':
            apiUrl = 'https://api.openai.com/v1/chat/completions';
            headers['Authorization'] = `Bearer ${config.apiKey}`;
            break;
          case 'openrouter':
            apiUrl = 'https://openrouter.ai/api/v1/chat/completions';
            headers['Authorization'] = `Bearer ${config.apiKey}`;
            headers['HTTP-Referer'] = window.location.origin;
            headers['X-Title'] = 'Website Analyzer';
            break;
          case 'custom':
            apiUrl = 'https://api.openai.com/v1/chat/completions';
            headers['Authorization'] = `Bearer ${config.apiKey}`;
            break;
        }

        // Make the API request
        const requestBody = {
          model: config.model,
          messages: [
            {
              role: 'system', 
              content: `${systemPrompt} 
                You are writing value-first, conversational email openers that follow a precise 8-step workflow. 
                Each opener MUST:
                1. Start with a specific insight using PERSONALIZATION TALKING POINTS (hook)
                2. Identify a problem relevant to their business (pain)
                3. Offer a solution structured by Template Strategy, Approach Style, Message Length, and Focus Area (outcome)
                4. Provide proof with specific metrics (credibility)
                5. End with a low-commitment, value-driven offer (CTA)
                
                Use the provided personalization talking points to show you know them personally. Structure your offer based on the specified strategy, style, length, and focus area. Keep it simple, conversational, and focused on delivering immediate value.`
            },
            {role: 'user', content: finalPrompt}
          ],
          max_tokens: 300,
          temperature: 0.7
        };

        console.log('Making AI request for opener with settings:', {provider: config.endpoint, model: config.model, style, length, focus, useNoThink: config.useNoThink});

        try {
          const response = await axios.post(apiUrl, requestBody, {headers});
          if (response.data && response.data.choices && response.data.choices[0]) {
            result = response.data.choices[0].message.content.trim();
          } else {
            console.error('Invalid API response structure:', response.data);
            throw new Error('Invalid API response structure');
          }
        } catch (apiError) {
          console.error('API request failed:', apiError);
          console.error('Response data:', apiError.response?.data);
          console.error('Response status:', apiError.response?.status);
          
          // Handle rate limiting (429) with retry
          if (apiError.response && apiError.response.status === 429) {
            throw new Error(`Rate limit exceeded. Please wait a moment and try again. (${apiError.response.status})`);
          }
          
          throw new Error(`API request failed: ${apiError.message}`);
        }
      }
      // For Ollama endpoint
      else if (config.endpoint === 'ollama') {
        if (!config.ollamaEndpoint || !config.model) {
          console.warn('No Ollama endpoint URL or model provided for AI opener generation');
          throw new Error('Ollama configuration required. Please add your endpoint URL and model in Settings.');
        }

        // Make the API request to Ollama
        const requestBody = {
          model: config.model,
          prompt: `${systemPrompt}\n\n${finalPrompt}\n\n
          IMPORTANT REMINDER: 
          1. Follow the 8-step workflow EXACTLY
          2. Start with a hook using PERSONALIZATION TALKING POINTS to show you know them (1 sentence)
          3. Structure your offer based on Template Strategy (${strategy}), Approach Style (${style}), Message Length (${length}), and Focus Area (${focus})
          4. End with a low-commitment, value-driven CTA that offers immediate value (1 sentence)
          5. Keep to 3-6 total sentences, each with a specific purpose
          6. Use simple language a 5th grader could understand
          7. Use the provided personalization talking points as quick references to demonstrate research`,
          stream: false
        };

        console.log('Making Ollama request for opener with settings:', {endpoint: config.ollamaEndpoint, model: config.model, style, length, focus, useNoThink: config.useNoThink});

        try {
          const response = await axios.post(config.ollamaEndpoint, requestBody);
          if (response.data && response.data.response) {
            result = response.data.response.trim();
          } else {
            console.error('Invalid Ollama API response structure:', response.data);
            throw new Error('Invalid Ollama API response structure');
          }
        } catch (ollamaError) {
          console.error('Ollama API request failed:', ollamaError);
          console.error('Response data:', ollamaError.response?.data);
          
          // Handle rate limiting (429) with retry
          if (ollamaError.response && ollamaError.response.status === 429) {
            throw new Error(`Rate limit exceeded. Please wait a moment and try again. (${ollamaError.response.status})`);
          }
          
          throw new Error(`Ollama API request failed: ${ollamaError.message}`);
        }
      } else {
        throw new Error('Unsupported endpoint type: ' + config.endpoint);
      }
    } catch (apiRequestError) {
      console.error('API request error:', apiRequestError);
      throw new Error(`API request failed: ${apiRequestError.message}`);
    }

    // Process the result
    if (result) {
      // Check if the result is a JSON string (possibly wrapped in markdown code blocks)
      const parsedJson = parseJsonResponse(result);
      
      if (parsedJson) {
        console.log('Parsed JSON response:', parsedJson);
        
        // Handle the specific JSON format with summary
        if (parsedJson.summary) {
          // Store the summary in the website data for future use
          websiteData.summary = parsedJson.summary;
          
          // Check if we have meaningful content to work with
          if (!parsedJson.summary || parsedJson.summary.trim().length < 20) {
            throw new Error('Unable to analyze website content - site may be blocking access or content unavailable');
          }
          
          // The summary should already be processed by the AI, so return it as is
          // This is a fallback case - normally the AI should return the opener directly
          return `Based on your company profile: ${parsedJson.summary.substring(0, 200)}... Would you like to discuss how we can help ${companyName}?`;
        }
      }
      
      // If not JSON or JSON parsing failed, process as regular text
      // If nothink option is enabled, remove empty <think></think> tags
      if (config.useNoThink) {
        result = result.replace(/<think>\s*<\/think>/g, '');
        result = result.replace(/<think><\/think>/g, '');
      }

      // Extract just the email body, removing subject line if present
      if (result.includes('Subject:')) {
        const parts = result.split(/\n+/);
        // Skip the subject line and any blank lines after it
        let i = 0;
        while (i < parts.length && (parts[i].trim() === '' || parts[i].includes('Subject:'))) {
          i++;
        }
        // Join the remaining parts
        result = parts.slice(i).join('\n').trim();
      }

      // Double-check that opener doesn't start with common greetings and remove them if they do
      const greetings = ['Hey', 'Hi', 'Hello', 'Greetings', 'Dear', 'I noticed', 'I saw', 'I was looking'];
      for (const greeting of greetings) {
        // Check for greeting followed by name, comma, or space
        const regex = new RegExp(`^${greeting}\\s+(?:${companyName})?[,\\s]`, 'i');
        if (result.match(regex)) {
          console.log('Removing greeting from opener');
          result = result.replace(regex, '');
          // Capitalize first letter after removing greeting
          result = result.charAt(0).toUpperCase() + result.slice(1);
        }
      }

      // Post-process the result to ensure it follows the structure
      // Count sentences (roughly)
      const sentences = result.split(/[.!?]+/).filter(s => s.trim().length > 0);
      
      // Verify we have enough sentences and that the last one is a question (CTA)
      if (sentences.length >= 3 && sentences.length <= 6) {
        // Check if the last sentence is a question
        const lastSentence = sentences[sentences.length - 1].trim();
        if (!lastSentence.endsWith('?')) {
          // If the last sentence is not a question, add a simple CTA
          result += ` I made a quick example showing how this would work for ${companyName} - can I send it over?`;
        }
      } else if (sentences.length < 3) {
        // If too few sentences, throw error instead of generic content
        throw new Error('Unable to generate meaningful opener - insufficient website content available');
      } else if (sentences.length > 6) {
        // If too many sentences, trim to the first 5 and add a CTA
        const trimmedSentences = sentences.slice(0, 5);
        result = trimmedSentences.join('. ') + '. ';
        result += `I put together a one-page example of what this could look like for ${companyName} - want me to share it?`;
      }

      return result;
    }

    throw new Error('Failed to generate opener: No result returned from API');
  } catch (error) {
    console.error('Error generating personalized opener with AI:', error);
    // Throw error instead of returning fallback message
    throw new Error(`Error generating opener: ${error.message}. Please check your API configuration and try again.`);
  }
};

/**
 * Regenerate opener for a specific website
 * @param {Object} website - Website data
 * @param {Object} config - API configuration
 * @param {Object} openerConfig - Optional opener generation settings
 * @returns {Promise<Object>} Result of the operation
 */
export const regenerateOpener = async (website, config, openerConfig = null) => {
  try {
    console.log('Starting opener regeneration with config:', config);
    console.log('Opener settings:', openerConfig);
    console.log('Website data:', website);

    // Create a copy of the website data to ensure we have all required fields
    const websiteData = {
      ...website, 
      company: website.company || extractDomain(website.url), 
      fitScore: website.fitScore || 5,
      qualificationResult: website.qualificationResult || null
    };

    // Check if we need to re-scrape data to get fresh content
    let updatedWebsiteData = websiteData;
    if (openerConfig?.refreshData) {
      // Re-scrape website data if needed
      if (openerConfig?.scrapingOptions?.scrapeWebsite !== false) {
        try {
          console.log('Scraping fresh website data for opener regeneration...');
          const scrapedData = await scrapeWebsite(websiteData.url, false); // Always false for screenshots
          updatedWebsiteData = {...websiteData, ...scrapedData};
          console.log('Successfully scraped fresh website data');
        } catch (scrapeError) {
          console.warn('Warning: Could not scrape fresh website data, using existing data:', scrapeError.message);
          // Continue with existing data if scraping fails - this is expected behavior
        }
      }
      
      // Re-scrape LinkedIn data if needed and available
      if (openerConfig?.scrapingOptions?.scrapeLinkedIn && websiteData.linkedinUrl && isValidLinkedInUrl(websiteData.linkedinUrl)) {
        try {
          console.log('Scraping fresh LinkedIn data for opener regeneration...');
          const linkedinUsername = extractLinkedInUsername(websiteData.linkedinUrl);
          if (linkedinUsername) {
            const linkedInData = await scrapeLinkedInProfile(websiteData.linkedinUrl);
            if (linkedInData.success && linkedInData.data) {
              // Use the standardized processing function
              const processedLinkedInData = processLinkedInDataForOpeners(linkedInData);
              if (processedLinkedInData) {
                updatedWebsiteData.linkedInData = processedLinkedInData;
                console.log('Successfully scraped fresh LinkedIn data');
              }
            }
          }
        } catch (linkedinError) {
          console.warn('Warning: Could not scrape fresh LinkedIn data, using existing data:', linkedinError.message);
          // Continue with existing data if LinkedIn scraping fails
        }
      }
      
      // Re-scrape talking points if needed and available
      if (openerConfig?.scrapingOptions?.scrapeTalkingPoints && websiteData.url && isValidWebsiteForTalkingPoints(websiteData.url)) {
        try {
          console.log('Scraping fresh talking points for opener regeneration...');
          const talkingPointsData = await scrapeTalkingPoints(websiteData.url);
          if (talkingPointsData.success && talkingPointsData.data) {
            updatedWebsiteData.talkingPointsData = talkingPointsData.data;
            console.log('Successfully scraped fresh talking points data');
          }
        } catch (talkingPointsError) {
          console.warn('Warning: Could not scrape fresh talking points, using existing data:', talkingPointsError.message);
          // Continue with existing data if talking points scraping fails
        }
      }
    }
    
    // Determine which strategy to use
    const strategy = openerConfig?.strategy || '8step';
    let result;
    
    if (strategy === '5line') {
      console.log('Using 5-line template strategy for opener regeneration');
      result = await regenerateFiveLineOpener(updatedWebsiteData, config, openerConfig);
      return result;
    } else if (strategy === '8step') {
      console.log('Using 8-step sequential strategy for opener regeneration');
      result = await regenerateSequentialOpener(updatedWebsiteData, config, openerConfig);
      return result;
    }
    
    // Fallback to the standard approach if strategy is not recognized
    // Generate new opener using AI with the specified settings
    const newOpener = await generatePersonalizedOpener(
      updatedWebsiteData,
      config,
      openerConfig
    );
    console.log('Generated new opener successfully');

    // Check if opener contains error message
    if (newOpener.startsWith('[Error') || newOpener.startsWith('[API configuration')) {
      return {
        success: false,
        error: newOpener.replace(/\[|\]/g, '')
      };
    }

    // Check if the AI response updated the website data with new information
    // This happens when the AI returns JSON with summary
    const updatedSummary = updatedWebsiteData.summary || websiteData.summary;

    // Note: localStorage update is handled by the calling component to avoid conflicts

    return {
      success: true,
      opener: newOpener,
      serviceType: openerConfig?.serviceType || 'website',
      subServiceType: openerConfig?.subServiceType || null,
      summary: updatedSummary
    };
  } catch (error) {
    console.error('Error regenerating opener:', error);
    return {
      success: false,
      error: error.message || 'Failed to regenerate opener'
    };
  }
};